import { useState, useCallback, useEffect, createContext, useContext, ReactNode } from 'react';

interface RequestLog {
  id: string;
  timestamp: Date;
  type: 'request' | 'response' | 'error';
  url: string;
  method?: string;
  status?: number;
  error?: string;
  duration?: number;
}

interface ConnectionContextType {
  isOffline: boolean;
  failedRequests: number;
  lastError: string | null;
  retryAll: () => void;
  clearErrors: () => void;
  logs: RequestLog[];
  addLog: (log: Omit<RequestLog, 'id' | 'timestamp'>) => void;
  registerRetryCallback: (callback: () => void) => () => void;
}

export const ConnectionContext = createContext<ConnectionContextType | null>(null);

const MAX_LOGS = 20;

export function ConnectionProvider({ children }: { children: ReactNode }) {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [failedRequests, setFailedRequests] = useState(0);
  const [lastError, setLastError] = useState<string | null>(null);
  const [logs, setLogs] = useState<RequestLog[]>([]);
  const [retryCallbacks, setRetryCallbacks] = useState<Set<() => void>>(new Set());

  // Track browser online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setFailedRequests(0);
      setLastError(null);
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const addLog = useCallback((log: Omit<RequestLog, 'id' | 'timestamp'>) => {
    const newLog: RequestLog = {
      ...log,
      id: Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
    };

    setLogs(prev => [newLog, ...prev].slice(0, MAX_LOGS));

    // Track failed requests
    if (log.type === 'error') {
      setFailedRequests(prev => prev + 1);
      setLastError(log.error || 'Request failed');
      
      // If we get multiple failed requests, assume we're offline
      if (log.error?.includes('Failed to fetch')) {
        setIsOffline(true);
      }
    } else if (log.type === 'response' && log.status && log.status >= 200 && log.status < 400) {
      // Successful response - we're online
      if (isOffline) {
        setIsOffline(false);
        setFailedRequests(0);
        setLastError(null);
      }
    }
  }, [isOffline]);

  const registerRetryCallback = useCallback((callback: () => void) => {
    setRetryCallbacks(prev => new Set(prev).add(callback));
    return () => {
      setRetryCallbacks(prev => {
        const newSet = new Set(prev);
        newSet.delete(callback);
        return newSet;
      });
    };
  }, []);

  const retryAll = useCallback(() => {
    setFailedRequests(0);
    setLastError(null);
    setIsOffline(false);
    
    // Call all registered retry callbacks
    retryCallbacks.forEach(callback => {
      try {
        callback();
      } catch (e) {
        console.error('Retry callback failed:', e);
      }
    });
  }, [retryCallbacks]);

  const clearErrors = useCallback(() => {
    setFailedRequests(0);
    setLastError(null);
  }, []);

  return (
    <ConnectionContext.Provider value={{
      isOffline,
      failedRequests,
      lastError,
      retryAll,
      clearErrors,
      logs,
      addLog,
      registerRetryCallback,
    }}>
      {children}
    </ConnectionContext.Provider>
  );
}

export function useConnectionStatus() {
  const context = useContext(ConnectionContext);
  if (!context) {
    throw new Error('useConnectionStatus must be used within a ConnectionProvider');
  }
  return context;
}
